import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import BRWStrip from '../components/BRWStrip';
import useSeo from '../hooks/useSeo';
import { IMAGES, HOME_PILLARS } from '../mock';

const Home = () => {
  useSeo({
    title: 'Duffy Investment Group | Boutique Investment Firm',
    description: 'Duffy Investment Group is a boutique investment firm with global experience across technology, property, gaming, entertainment and diverse investment sectors.',
    keywords: 'Duffy Investment Group, boutique investment firm, private investments, global investments, technology investments, property investments, gaming investments, entertainment investments',
  });

  return (
    <div>
      {/* ===================== HERO ===================== */}
      <section className="relative bg-navy-grad overflow-hidden">
        <div className="absolute inset-0 lg:left-[42%]">
          <img
            src={IMAGES.heroBusinessman}
            alt="Craig Duffy, Executive Chairman"
            className="w-full h-full craig-hero-img"
          />
          {/* mobile: keep face clear at top, darken lower half for text */}
          <div
            className="absolute inset-0 lg:hidden"
            style={{ background: 'linear-gradient(180deg, rgba(7,19,34,0.15) 0%, rgba(7,19,34,0) 30%, rgba(7,19,34,0.55) 55%, rgba(7,19,34,0.96) 100%)' }}
          />
          <div
            className="absolute inset-0 hidden lg:block"
            style={{ background: 'linear-gradient(90deg, rgba(10,26,48,1) 0%, rgba(10,26,48,0.6) 16%, rgba(10,26,48,0) 42%)' }}
          />
          <div
            className="absolute inset-0 hidden lg:block"
            style={{ background: 'linear-gradient(0deg, rgba(7,19,34,0.7) 0%, rgba(7,19,34,0) 35%)' }}
          />
          <div className="hero-br-tint" />
        </div>

        <div className="relative dig-container">
          <div className="flex flex-col justify-end lg:justify-center min-h-[86vh] lg:min-h-[640px]">
            <div className="max-w-xl pb-10 lg:pb-0">
              <Reveal>
                <h1
                  className="font-serif-dig text-white"
                  style={{ fontSize: 'clamp(1.9rem, 6vw, 3.5rem)', fontWeight: 700, lineHeight: 1.12 }}
                >
                  A Boutique Investment Firm with <span className="gold-text">Global Reach</span> and <span className="gold-text">Local Insight</span>
                </h1>
              </Reveal>
              <Reveal delay={120}>
                <span className="gold-rule-lg block my-6" />
                <p className="font-body" style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.05rem', lineHeight: 1.7, maxWidth: 440 }}>
                  Strategic investments in quality businesses and markets worldwide.
                </p>
              </Reveal>
              <Reveal delay={240}>
                <div className="flex flex-col sm:flex-row gap-4 mt-8">
                  <Link to="/about" className="btn-gold justify-center">ABOUT US</Link>
                  <Link to="/our-investments" className="btn-outline justify-center">OUR INVESTMENTS</Link>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="hidden lg:block absolute" style={{ bottom: 44, right: 20 }}>
            <div className="text-right" style={{ textShadow: '0 2px 14px rgba(0,0,0,0.9)' }}>
              <p className="font-serif-dig gold-text" style={{ fontSize: '1.6rem', fontWeight: 700, letterSpacing: '0.05em' }}>CRAIG DUFFY</p>
              <p className="font-body" style={{ color: 'var(--gold-light)' }}>Executive Chairman</p>
              <p className="font-body" style={{ color: 'rgba(255,255,255,0.92)' }}>Duffy Investment Group</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== PILLARS ===================== */}
      <section className="bg-cream">
        <div className="dig-container py-14 md:py-16">
          <div className="grid md:grid-cols-3 gap-10 md:gap-0">
            {HOME_PILLARS.map((p, i) => (
              <Reveal
                key={p.title}
                delay={i * 120}
                className={`flex gap-5 items-start md:px-9 ${i > 0 ? 'md:border-l' : ''}`}
              >
                <div style={i > 0 ? { borderColor: 'rgba(0,0,0,0.1)' } : {}} className="flex gap-5 items-start">
                  <Icon name={p.icon} size={42} strokeWidth={1.3} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                  <div>
                    <h3 className="font-label navy-text" style={{ fontWeight: 700, letterSpacing: '0.06em', fontSize: '0.9rem' }}>{p.title}</h3>
                    <p className="font-body mt-2" style={{ color: 'var(--muted)', lineHeight: 1.6 }}>{p.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== BRW ===================== */}
      <BRWStrip />

      {/* ===================== BUILDING VALUE ===================== */}
      <section>
        <div className="grid lg:grid-cols-2">
          <div className="relative" style={{ minHeight: 380 }}>
            <img src={IMAGES.sydney} alt="City skyline at dusk" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="bg-cream-2 flex items-center">
            <div className="px-6 sm:px-12 py-16 max-w-xl">
              <Reveal>
                <h2 className="section-title" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.1rem)', lineHeight: 1.2 }}>
                  BUILDING VALUE.<br />CREATING OPPORTUNITY.
                </h2>
                <span className="gold-rule block mt-5 mb-6" />
                <p className="font-body" style={{ color: 'var(--muted)', fontSize: '1.05rem', lineHeight: 1.75 }}>
                  Partnering with exceptional businesses and management teams to achieve long-term, sustainable growth.
                </p>
                <Link to="/our-investments" className="btn-gold mt-8">
                  LEARN MORE <ArrowRight size={16} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
